import WebsiteRepositoryInterface, {
  Website,
  WebsiteComposite,
  WebsiteCreation,
  WebsiteEnvironment,
  WebsiteStatus,
} from '@/services/Website/interfaces';
import WebsiteStagingRepositoryInterface from '@/services/WebsiteStaging/interfaces';
import WebsiteMigrationsRepositoryInterface from '@/services/WebsiteMigrations/interfaces';
import WPCliRepositoryInterface from '@/services/WPCli/interfaces';
import DNSRepositoryInterface, {
  DNSRecordsInterface,
  DNSZonesInterface,
} from '@/services/DNS/Interfaces';
import { CertificatesRepositoryInterface, CertificateInterface } from '@/services/SSL/interfaces';
import { DatabaseRepositoryInterface, DatabaseInterface } from '@/services/Database/interfaces';
import {
  MailboxRepositoryInterface,
  MailboxInterface,
  MailboxStatus,
} from '@/services/Mailbox/interfaces';
import { FtpUserRepositoryInterface } from '@/services/FtpUser/interfaces';
import OriginVhostRepositoryInterface from '@/services/Vhost/Origin/Interfaces';
import RedisCacheRepositoryInterface, {
  RedisCacheStatusEnum,
} from '@/services/RedisCache/interfaces/redis-cache.interface';
import { SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import {
  ConsoleActionId,
  evaluateActionAvailability,
  getConsoleAction,
  getRetryActionMeta,
  listAvailableActions,
} from './actions';
import { contextFromWebsite } from './history';
import { isNumericId, requireTarget } from './parser';
import {
  ConsoleActionListItem,
  ConsoleActionPreview,
  ConsoleActionResult,
  ConsoleExecuteOutcome,
  ConsoleNavigateTarget,
  ConsoleSiteContext,
} from './types';

export interface ConsoleActionExecutorDeps {
  websiteRepository: WebsiteRepositoryInterface;
  websiteStagingRepository: WebsiteStagingRepositoryInterface;
  wpCliRepository: WPCliRepositoryInterface;
  websiteMigrationsRepository: WebsiteMigrationsRepositoryInterface;
  dnsRepository: DNSRepositoryInterface;
  certificateRepository: CertificatesRepositoryInterface;
  databaseRepository: DatabaseRepositoryInterface;
  mailboxRepository: MailboxRepositoryInterface;
  ftpUserRepository: FtpUserRepositoryInterface;
  originVhostRepository: OriginVhostRepositoryInterface;
  redisCacheRepository: RedisCacheRepositoryInterface;
  getUserId: () => string;
  getScopes: () => string[];
  context: ConsoleSiteContext | null;
  systemUserRepository?: SystemUserRepositoryInterface;
  serverRepository?: ServerRepositoryInterface;
  getJwt?: () => string;
}

export interface ConsoleActionMutationOutcome {
  result: ConsoleActionResult;
  navigateTo?: ConsoleNavigateTarget;
  refreshContext?: boolean;
  setContext?: ConsoleSiteContext | null;
  openUrl?: string;
}

const MAILBOX_STATUS_LABELS = [
  'CREATING',
  'CREATED',
  'UPDATING',
  'ERROR',
  'ACTIVE',
  'DISABLED',
];

const DEFAULT_PHP_VERSIONS = ['php7.4', 'php8.0', 'php8.1', 'php8.2', 'php8.3'];

export function apiErrorMessage(error: any, fallback: string): string {
  if (error && error.response) {
    const status = error.response.status;
    const data = error.response.data;
    const msg = data && (data.message || data.error || data.Error);
    if (status === 403) {
      return msg ? String(msg) : 'Permission denied.';
    }
    if (msg) {
      return String(msg);
    }
    if (status >= 500) {
      return `Server error (${status}).`;
    }
    return `Request failed (${status}).`;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function isNotFound(error: any): boolean {
  return !!(error && error.response && error.response.status === 404);
}

function isForbidden(error: any): boolean {
  return !!(error && error.response && error.response.status === 403);
}

async function searchByDomain(
  repository: WebsiteRepositoryInterface,
  domain: string,
): Promise<{ websites: Website[]; count: number }> {
  const response = await repository.searchWebsites(1, domain, undefined, undefined, undefined, 25);
  return {
    websites: response.Websites || [],
    count: response.count || 0,
  };
}

async function resolveWebsiteChoices(
  repository: WebsiteRepositoryInterface,
  query: string,
): Promise<{ exact?: WebsiteComposite; websites: Website[]; count: number; error?: string }> {
  if (isNumericId(query)) {
    try {
      const composite = await repository.getWebsiteById(parseInt(query, 10));
      return { exact: composite, websites: [composite.website], count: 1 };
    } catch (error) {
      if (isNotFound(error)) {
        return { websites: [], count: 0 };
      }
      return { websites: [], count: 0, error: apiErrorMessage(error, 'Failed to load website.') };
    }
  }

  try {
    const { websites, count } = await searchByDomain(repository, query);
    const q = query.toLowerCase();
    const exactMatches = websites.filter((site) => site.domain.toLowerCase() === q);

    if (exactMatches.length === 1) {
      try {
        const composite = await repository.getWebsiteById(exactMatches[0].id);
        return { exact: composite, websites: [composite.website], count: 1 };
      } catch (error) {
        if (isNotFound(error)) {
          return { websites: exactMatches, count: 1 };
        }
        return { websites: [], count: 0, error: apiErrorMessage(error, 'Failed to load website.') };
      }
    }

    if (exactMatches.length > 1) {
      return { websites: exactMatches, count: exactMatches.length };
    }

    return { websites, count };
  } catch (error) {
    return { websites: [], count: 0, error: apiErrorMessage(error, 'Search failed.') };
  }
}

function buildPreview(
  actionId: ConsoleActionId,
  composite: WebsiteComposite | null,
): ConsoleActionPreview | undefined {
  const action = getConsoleAction(actionId);
  if (!action) {
    return undefined;
  }
  const target = composite
    ? `${composite.website.domain} (#${composite.website.id})`
    : 'no website context';
  const sideEffects: string[] = [];
  if (action.risk === 'destructive' || action.risk === 'high') {
    sideEffects.push('This action may be difficult to undo.');
  }
  if (action.requireTypedDomain) {
    sideEffects.push('Requires typing the domain to confirm.');
  }
  return {
    target,
    summary: action.description,
    sideEffects,
    risk: action.risk,
  };
}

function baseActionResult(
  actionId: ConsoleActionId,
  composite: WebsiteComposite | null,
  extras?: Partial<ConsoleActionResult>,
): ConsoleActionResult {
  const action = getConsoleAction(actionId);
  return {
    kind: 'action',
    actionId,
    composite,
    phase: 'form',
    panel: action ? action.panel : '',
    preview: buildPreview(actionId, composite),
    requireTypedDomain: action ? !!action.requireTypedDomain : false,
    ...extras,
  };
}

function actionError(
  actionId: ConsoleActionId,
  composite: WebsiteComposite | null,
  message: string,
  data?: any,
): ConsoleActionResult {
  return baseActionResult(actionId, composite, {
    phase: 'error',
    errorMessage: message,
    data,
  });
}

function actionSuccess(
  actionId: ConsoleActionId,
  composite: WebsiteComposite | null,
  message: string,
  extras?: Partial<ConsoleActionResult>,
): ConsoleActionResult {
  return baseActionResult(actionId, composite, {
    phase: 'success',
    message,
    ...extras,
  });
}

function formWithMessage(
  actionId: ConsoleActionId,
  composite: WebsiteComposite | null,
  data: any,
  message: string,
  extras?: Partial<ConsoleActionResult>,
): ConsoleActionResult {
  return baseActionResult(actionId, composite, {
    phase: 'form',
    data,
    message,
    errorMessage: undefined,
    ...extras,
  });
}

function formWithError(
  actionId: ConsoleActionId,
  composite: WebsiteComposite | null,
  data: any,
  errorMessage: string,
  extras?: Partial<ConsoleActionResult>,
): ConsoleActionResult {
  return baseActionResult(actionId, composite, {
    phase: 'form',
    data,
    errorMessage,
    ...extras,
  });
}

async function loadCreations(
  deps: ConsoleActionExecutorDeps,
  websiteId: number,
): Promise<WebsiteCreation[]> {
  try {
    return await deps.websiteRepository.getWebsiteCreations(websiteId);
  } catch (error) {
    if (isForbidden(error) || isNotFound(error)) {
      return [];
    }
    throw error;
  }
}

function dnsRecordDisplayValue(record: DNSRecordsInterface): string {
  const content = record.content || ({} as any);
  if (String(record.type) === 'SOA') {
    return [
      content.target,
      content.responsible_domain,
      content.serial_number,
      content.refresh_interval,
      content.retry_interval,
      content.expire_interval,
      content.neg_cache_ttl,
    ]
      .filter((value) => value !== undefined && value !== null && value !== '')
      .join(' ');
  }
  if (String(record.type) === 'CAA') {
    return [content.flag, content.tag, content.target ? `${content.target}` : '']
      .filter((value) => value !== undefined && value !== null && value !== '')
      .join(' ');
  }
  if (content.priority !== undefined && content.priority !== null) {
    return `${content.priority} ${content.target || record.target || ''}`.trim();
  }
  return content.target || record.target || '';
}

function mapDnsRecords(records: DNSRecordsInterface[]) {
  return records.map((record) => ({
    id: record.id,
    name: (record.content && record.content.host) || record.target || '',
    type: String(record.type),
    content: dnsRecordDisplayValue(record),
    ttl: (record.content && record.content.ttl) || 0,
  }));
}

async function pickDnsZone(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<{ zone?: DNSZonesInterface; zoneError?: string }> {
  try {
    const zones = await deps.dnsRepository.searchZones(composite.website.domain);
    const zone = zones.find((item) => item.websiteId === composite.website.id)
      || zones.find((item) =>
        (item.installation && item.installation.domain === composite.website.domain)
        || item.domain === composite.website.domain)
      || zones[0];
    if (!zone) {
      return { zoneError: `No DNS zone found for ${composite.website.domain}.` };
    }
    return { zone };
  } catch (error) {
    return { zoneError: apiErrorMessage(error, 'Failed to load DNS zone.') };
  }
}

async function loadDnsManageData(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  const picked = await pickDnsZone(deps, composite);
  if (!picked.zone) {
    return { zoneError: picked.zoneError || 'No DNS zone found.', records: [] };
  }
  try {
    const records = await deps.dnsRepository.getDNSZoneRecords(picked.zone.id);
    return {
      zoneId: picked.zone.id,
      zoneDomain: (picked.zone.installation && picked.zone.installation.domain)
        || picked.zone.domain
        || composite.website.domain,
      records: mapDnsRecords(records || []),
    };
  } catch (error) {
    return {
      zoneId: picked.zone.id,
      zoneDomain: (picked.zone.installation && picked.zone.installation.domain)
        || picked.zone.domain
        || composite.website.domain,
      records: [],
      zoneError: apiErrorMessage(error, 'Failed to load DNS records.'),
    };
  }
}

function sanitizeCertificate(certificate: CertificateInterface) {
  return {
    id: certificate.id,
    type: certificate.type,
    issueDate: certificate.issuedAt || '',
    expireDate: certificate.expiresAt || '',
    autoRenew: !!certificate.autoRenew,
    altNames: certificate.altNames || [],
    websiteId: certificate.websiteId,
  };
}

async function loadSslManageData(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  const certId = composite.details && composite.details.sslCertificateId;
  try {
    let certificate: CertificateInterface | undefined;
    if (certId) {
      certificate = await deps.certificateRepository.getCertificateById(certId);
    } else {
      certificate = await deps.certificateRepository.getCertificate(composite.website.domain);
    }
    if (!certificate) {
      return { certificate: null };
    }
    return {
      certificate: sanitizeCertificate(certificate),
      logs: (certificate.logs || []).map((log) => ({
        id: log.id,
        log: log.message || '',
      })),
    };
  } catch (error) {
    if (isNotFound(error)) {
      return { certificate: null };
    }
    return {
      certificate: null,
      sslError: apiErrorMessage(error, 'Failed to load SSL certificate.'),
    };
  }
}

async function loadDbManageData(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  try {
    const databases = await deps.databaseRepository.getByWebsiteId(composite.website.id);
    return {
      databases: (databases || []).map((database: DatabaseInterface) => ({
        id: database.id,
        name: database.name,
        size: database.size,
        users: (database.users || []).map((user) => ({ id: user.id, username: user.username })),
      })),
    };
  } catch (error) {
    return {
      databases: [],
      dbError: apiErrorMessage(error, 'Failed to load databases.'),
    };
  }
}

async function findMailboxForWebsite(
  deps: ConsoleActionExecutorDeps,
  websiteId: number,
): Promise<MailboxInterface | null> {
  const [mailboxes] = await deps.mailboxRepository.searchMailbox(1, String(websiteId));
  const match = (mailboxes || []).find((mailbox) => mailbox.websiteId === websiteId);
  return match || null;
}

function mapMailboxUsers(mailbox: MailboxInterface | null) {
  if (!mailbox || !mailbox.users) {
    return [];
  }
  return mailbox.users.map((user) => ({
    id: user.id,
    email: user.address || user.username,
    status: MAILBOX_STATUS_LABELS[user.status] || String(user.status),
    quota: user.quota ? user.quota.used : undefined,
  }));
}

async function loadMailManageData(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  try {
    const mailbox = await findMailboxForWebsite(deps, composite.website.id);
    if (!mailbox) {
      return { activated: false, mailbox: null, users: [] };
    }
    return {
      activated: true,
      mailbox: { id: mailbox.id, server: mailbox.server, status: mailbox.status },
      users: mapMailboxUsers(mailbox),
    };
  } catch (error) {
    return {
      activated: false,
      mailbox: null,
      users: [],
      mailError: apiErrorMessage(error, 'Failed to load mailboxes.'),
    };
  }
}

async function loadFtpManageData(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  const unixUserId = composite.details && composite.details.unixUserId;
  if (!unixUserId) {
    return { users: [], ftpError: 'Website has no unix user.' };
  }
  if (!deps.systemUserRepository) {
    return { users: [], ftpError: 'System user repository is unavailable.' };
  }
  try {
    const systemUser = await deps.systemUserRepository.findSystemUserById(unixUserId);
    const search = systemUser && systemUser.name;
    if (!search) {
      return { users: [], systemUserId: unixUserId };
    }

    const allFtpUsers: Array<{ id: number; username: string; homeDir?: string }> = [];
    let page = 1;
    let total = Number.POSITIVE_INFINITY;

    while (allFtpUsers.length < total) {
      const searchResult = await deps.ftpUserRepository.searchFtpUsers(page, search);
      total = searchResult.total;
      (searchResult.ftpUsers || []).forEach((ftpUser) => {
        if (ftpUser.websiteId === composite.website.id) {
          allFtpUsers.push({
            id: ftpUser.id,
            username: ftpUser.username,
            homeDir: ftpUser.homeDir,
          });
        }
      });
      if (!searchResult.ftpUsers.length || !searchResult.next) {
        break;
      }
      page += 1;
    }

    return {
      users: allFtpUsers,
      systemUserId: unixUserId,
      systemUserDirectory: systemUser.directory,
    };
  } catch (error) {
    return {
      users: [],
      ftpError: apiErrorMessage(error, 'Failed to load FTP users.'),
    };
  }
}

function normalizePhpVersionLabel(version: number | string): string {
  if (typeof version === 'string') {
    return version.startsWith('php') ? version : `php${version}`;
  }
  const fixed = Number(version).toFixed(1);
  return `php${fixed}`;
}

function parsePhpVersionNumber(value: string): number {
  const raw = value.startsWith('php') ? value.substr(3) : value;
  return Number(raw);
}

async function loadVhostManageData(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  try {
    const vhost = await deps.originVhostRepository.getVhostByWebsiteId(composite.website.id);
    let phpVersions = DEFAULT_PHP_VERSIONS.slice();
    if (deps.serverRepository) {
      try {
        const server = await deps.serverRepository.getServer(composite.website.originServer);
        const fromServer = (server.services || []).filter((service) => service.startsWith('php'));
        if (fromServer.length) {
          phpVersions = fromServer;
        }
      } catch (_e) {
        // Keep defaults.
      }
    }
    const current = vhost.php ? normalizePhpVersionLabel(vhost.php.version) : '';
    if (current && phpVersions.indexOf(current) === -1) {
      phpVersions = [current].concat(phpVersions);
    }
    return {
      vhost: {
        id: vhost.id,
        aliases: (vhost.aliases || []).map((alias) => alias.alias),
        php: { version: current },
        logs: (vhost.logs || []).map((log) => ({ id: log.id, log: log.log })),
      },
      phpVersions,
    };
  } catch (error) {
    return {
      vhostError: apiErrorMessage(error, 'Failed to load vhost.'),
    };
  }
}

function redisStatusLabel(status: RedisCacheStatusEnum): string {
  switch (status) {
    case RedisCacheStatusEnum.ENABLED:
      return 'enabled';
    case RedisCacheStatusEnum.DISABLED:
      return 'disabled';
    case RedisCacheStatusEnum.PENDING_ENABLE:
      return 'pending enable';
    case RedisCacheStatusEnum.PENDING_DISABLE:
      return 'pending disable';
    default:
      return 'unknown';
  }
}

async function loadRedisManageData(
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  try {
    const result = await deps.redisCacheRepository.getRedisCacheStatusByWebsiteId(
      composite.website.id,
    );
    const status = result.status;
    return {
      status: redisStatusLabel(status),
      enabled: status === RedisCacheStatusEnum.ENABLED
        || status === RedisCacheStatusEnum.PENDING_ENABLE,
    };
  } catch (error) {
    if (isNotFound(error)) {
      return { status: 'disabled', enabled: false };
    }
    return {
      status: 'unknown',
      enabled: false,
      redisError: apiErrorMessage(error, 'Failed to load Redis status.'),
    };
  }
}

async function preloadManageData(
  actionId: ConsoleActionId,
  deps: ConsoleActionExecutorDeps,
  composite: WebsiteComposite,
): Promise<any> {
  switch (actionId) {
    case 'dns.manage':
      return loadDnsManageData(deps, composite);
    case 'ssl.manage':
      return loadSslManageData(deps, composite);
    case 'db.manage':
      return loadDbManageData(deps, composite);
    case 'mail.manage':
      return loadMailManageData(deps, composite);
    case 'ftp.manage':
      return loadFtpManageData(deps, composite);
    case 'vhost.manage':
      return loadVhostManageData(deps, composite);
    case 'redis.manage':
      return loadRedisManageData(deps, composite);
    default:
      return undefined;
  }
}

function navigateForOpen(
  actionId: ConsoleActionId,
  composite: WebsiteComposite,
): ConsoleNavigateTarget | null {
  if (actionId === 'open.server') {
    return {
      name: 'server_view',
      params: { server: composite.website.originServer },
    };
  }
  if (actionId === 'open.group' && composite.group) {
    return {
      name: 'website_group_view',
      params: { id: String(composite.group.id) },
    };
  }
  if (actionId === 'open.unix' && composite.details && composite.details.unixUserId) {
    return {
      name: 'system_user_edit',
      params: { systemUserId: String(composite.details.unixUserId) },
    };
  }
  return null;
}

export async function openConsoleAction(
  actionId: ConsoleActionId,
  deps: ConsoleActionExecutorDeps,
  query?: string,
  args?: string[],
): Promise<ConsoleExecuteOutcome> {
  const action = getConsoleAction(actionId);
  if (!action) {
    return { result: { kind: 'error', message: `Unknown action: ${actionId}` } };
  }

  let composite: WebsiteComposite | null = null;
  let setContext: ConsoleSiteContext | undefined;

  if (action.requiresContext || actionId === 'actions.list' || query) {
    const target = requireTarget(
      query,
      deps.context ? deps.context.id : undefined,
      action.command.replace(/^\//, '').split(' ')[0] || 'action',
    );

    if (!('error' in target)) {
      const resolved = await resolveWebsiteChoices(deps.websiteRepository, target.query);
      if (resolved.error) {
        return { result: { kind: 'error', message: resolved.error } };
      }
      if (resolved.exact) {
        composite = resolved.exact;
        setContext = contextFromWebsite(composite.website);
      } else if (resolved.websites.length > 1) {
        return {
          result: {
            kind: 'choices',
            websites: resolved.websites,
            count: resolved.count,
            message: `Multiple websites matched "${target.query}". Pick one:`,
            intendedCommand: action.command.replace(/^\//, ''),
          },
        };
      } else if (query || action.requiresContext) {
        if (!resolved.websites.length) {
          return {
            result: {
              kind: 'empty',
              message: `No website matched "${target.query}".`,
            },
          };
        }
      }
    } else if (action.requiresContext) {
      return { result: { kind: 'error', message: target.error } };
    }
  }

  let creations: WebsiteCreation[] | undefined;
  const needsCreations = actionId === 'website.recreate'
    || actionId === 'website.retry'
    || actionId === 'actions.list';

  if (needsCreations && composite) {
    try {
      creations = await loadCreations(deps, composite.website.id);
    } catch (error) {
      return {
        result: {
          kind: 'error',
          message: apiErrorMessage(error, 'Failed to load creation records.'),
        },
        setContext,
      };
    }
  }

  const availability = evaluateActionAvailability(
    action,
    composite,
    deps.getScopes(),
    { creations },
  );

  if (!availability.available) {
    return {
      result: {
        kind: 'error',
        message: availability.disabledReason || 'Action is not available.',
      },
      setContext,
    };
  }

  if (actionId === 'open.server' || actionId === 'open.group' || actionId === 'open.unix') {
    if (!composite) {
      return {
        result: { kind: 'error', message: 'Set context with /use <domain-or-id> first.' },
      };
    }
    const navigateTo = navigateForOpen(actionId, composite);
    if (!navigateTo) {
      return {
        result: { kind: 'error', message: 'Unable to resolve navigation target.' },
        setContext,
      };
    }
    return {
      result: {
        kind: 'text',
        message: `Opening ${navigateTo.name}…`,
      },
      navigateTo,
      setContext,
    };
  }

  if (actionId === 'actions.list') {
    const items: ConsoleActionListItem[] = listAvailableActions(
      composite,
      deps.getScopes(),
      { creations },
    ).map((item) => ({
      id: item.action.id,
      label: item.action.label,
      group: item.action.group,
      command: item.action.command,
      disabled: !item.available,
      disabledReason: item.disabledReason,
      risk: item.action.risk,
    }));

    return {
      result: baseActionResult(actionId, composite, {
        panel: 'actions-list',
        actions: items,
      }),
      setContext,
    };
  }

  if (!composite) {
    return {
      result: { kind: 'error', message: 'Set context with /use <domain-or-id> first.' },
    };
  }

  let data: any;
  let meta: { [key: string]: any } | undefined;

  try {
    if (
      actionId === 'dns.manage'
      || actionId === 'ssl.manage'
      || actionId === 'db.manage'
      || actionId === 'mail.manage'
      || actionId === 'ftp.manage'
      || actionId === 'vhost.manage'
      || actionId === 'redis.manage'
    ) {
      data = await preloadManageData(actionId, deps, composite);
    }

    if (actionId === 'website.retry') {
      if (args && args.length >= 2 && isNumericId(args[0]) && isNumericId(args[1])) {
        meta = {
          creationId: parseInt(args[0], 10),
          actionId: parseInt(args[1], 10),
          actionName: `action #${args[1]}`,
        };
      } else {
        const retryMeta = getRetryActionMeta(creations);
        if (!retryMeta) {
          return {
            result: {
              kind: 'error',
              message: 'No retryable failed creation action.',
            },
            setContext,
          };
        }
        meta = retryMeta;
      }
    }
  } catch (error) {
    return {
      result: {
        kind: 'error',
        message: apiErrorMessage(error, 'Failed to prepare action.'),
      },
      setContext,
    };
  }

  return {
    result: baseActionResult(actionId, composite, { data, meta }),
    setContext,
  };
}

async function mutateWebsiteAction(
  actionId: ConsoleActionId,
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const userId = deps.getUserId();
  const websiteId = composite.website.id;

  switch (actionId) {
    case 'staging.create': {
      const environment = payload && payload.environment ? String(payload.environment) : 'clone';
      if (environment === 'new') {
        await deps.wpCliRepository.saveTempCredentials({
          domain: `staging.${composite.website.domain}`,
          email: String(payload.email || ''),
          user: String(payload.user || ''),
          password: String(payload.password || ''),
        });
      }
      await deps.websiteRepository.createWebsiteStaging(websiteId, environment, userId);
      return {
        result: actionSuccess(actionId, composite, 'Staging creation started.'),
        refreshContext: true,
      };
    }
    case 'staging.merge': {
      await deps.websiteStagingRepository.mergeStaging(websiteId, userId);
      return {
        result: actionSuccess(actionId, composite, 'Staging merge started.'),
        navigateTo: { name: 'website_all' },
        setContext: null,
      };
    }
    case 'alias.create': {
      const domain = String(payload.domain || '');
      const excludeActions = Array.isArray(payload.excludeActions) ? payload.excludeActions : ['email'];
      await deps.websiteRepository.createWebsiteAlias(
        websiteId,
        domain,
        excludeActions,
        WebsiteEnvironment.ALIAS,
        userId,
      );
      return {
        result: actionSuccess(actionId, composite, `Alias creation started for ${domain}.`),
        refreshContext: true,
      };
    }
    case 'subdomain.create': {
      const domain = String(payload.domain || '');
      const excludeActions = Array.isArray(payload.excludeActions)
        ? payload.excludeActions
        : ['wp', 'db', 'email'];
      await deps.websiteRepository.createSubwebsite(
        websiteId,
        domain,
        userId,
        WebsiteEnvironment.SUB,
        excludeActions,
      );
      return {
        result: actionSuccess(actionId, composite, `Subdomain creation started for ${domain}.`),
        refreshContext: true,
      };
    }
    case 'wp.install': {
      await deps.wpCliRepository.installWp({
        title: String(payload.title || ''),
        user: String(payload.user || ''),
        password: String(payload.password || ''),
        email: String(payload.email || ''),
        forceInstall: !!payload.forceInstall,
        websiteId,
        directory: payload.directory ? String(payload.directory) : undefined,
      });
      return {
        result: actionSuccess(actionId, composite, 'WordPress installation started.'),
      };
    }
    case 'wp.uninstall': {
      await deps.wpCliRepository.uninstallWp(websiteId);
      return {
        result: actionSuccess(actionId, composite, 'WordPress uninstallation started.'),
      };
    }
    case 'website.enable':
    case 'website.disable': {
      const status = payload && payload.status
        ? String(payload.status)
        : (actionId === 'website.enable' ? WebsiteStatus.ACTIVE : WebsiteStatus.DISABLED);
      await deps.websiteRepository.toggleWebsite(websiteId, status);
      return {
        result: actionSuccess(
          actionId,
          composite,
          status === WebsiteStatus.ACTIVE ? 'Website enable started.' : 'Website disable started.',
        ),
        refreshContext: true,
      };
    }
    case 'website.rename': {
      const newDomain = String(payload.newDomain || '');
      const excludeActions = Array.isArray(payload.excludeActions) ? payload.excludeActions : [];
      await deps.websiteMigrationsRepository.renameWebsite(websiteId, newDomain, excludeActions);
      return {
        result: actionSuccess(actionId, composite, `Rename to ${newDomain} started.`),
        navigateTo: { name: 'website_migrations_list' },
        refreshContext: true,
      };
    }
    case 'website.delete': {
      await deps.websiteRepository.deleteWebsite(websiteId);
      return {
        result: actionSuccess(actionId, composite, 'Website deletion started.'),
        refreshContext: true,
      };
    }
    case 'website.recreate': {
      await deps.websiteRepository.recreateWebsite(websiteId);
      return {
        result: actionSuccess(actionId, composite, 'Website recreation started.'),
        refreshContext: true,
      };
    }
    case 'website.retry': {
      const creationId = Number(payload.creationId);
      const actionItemId = Number(payload.actionId);
      await deps.websiteRepository.retryAction(websiteId, creationId, actionItemId);
      return {
        result: actionSuccess(actionId, composite, 'Creation action retry started.'),
        refreshContext: true,
      };
    }
    default:
      return {
        result: actionError(actionId, composite, `Unhandled website action: ${actionId}`),
      };
  }
}

async function mutateDnsManage(
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const actionId: ConsoleActionId = 'dns.manage';
  const operation = payload && payload.operation ? String(payload.operation) : '';
  const currentData = await loadDnsManageData(deps, composite);

  try {
    if (operation === 'refresh') {
      return {
        result: formWithMessage(actionId, composite, currentData, 'DNS records refreshed.'),
      };
    }

    if (operation === 'sync') {
      const domain = currentData.zoneDomain || composite.website.domain;
      await deps.dnsRepository.syncDNS(domain, undefined, 0);
      const data = await loadDnsManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, data, 'DNS sync started.'),
      };
    }

    if (operation === 'delete') {
      const zoneId = currentData.zoneId;
      const recordId = payload.recordId;
      if (!zoneId) {
        return {
          result: formWithError(actionId, composite, currentData, 'No DNS zone loaded.'),
        };
      }
      await deps.dnsRepository.deleteDNSRecord(Number(zoneId), Number(recordId));
      const data = await loadDnsManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, data, `DNS record #${recordId} deleted.`),
      };
    }

    if (operation === 'create-navigate') {
      if (!currentData.zoneId) {
        return {
          result: formWithError(actionId, composite, currentData, 'No DNS zone loaded.'),
        };
      }
      return {
        result: formWithMessage(actionId, composite, currentData, 'Opening DNS record create…'),
        navigateTo: {
          name: 'dns_record_create',
          params: { id: String(currentData.zoneId) },
        },
      };
    }

    return {
      result: formWithError(actionId, composite, currentData, `Unknown DNS operation: ${operation}`),
    };
  } catch (error) {
    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        apiErrorMessage(error, 'DNS operation failed.'),
      ),
    };
  }
}

async function mutateSslManage(
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const actionId: ConsoleActionId = 'ssl.manage';
  const operation = payload && payload.operation ? String(payload.operation) : '';
  let currentData = await loadSslManageData(deps, composite);

  try {
    if (operation === 'renew') {
      if (!currentData.certificate) {
        return {
          result: formWithError(actionId, composite, currentData, 'No certificate loaded.'),
        };
      }
      await deps.certificateRepository.renewCertificate(currentData.certificate.id);
      currentData = await loadSslManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Certificate renewal started.'),
      };
    }

    if (operation === 'delete') {
      const websiteIdForDelete = (currentData.certificate && currentData.certificate.websiteId)
        || composite.website.id;
      await deps.certificateRepository.deleteCertificate(websiteIdForDelete);
      currentData = await loadSslManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Certificate deletion started.'),
        refreshContext: true,
      };
    }

    if (operation === 'create-navigate') {
      const navigateTo: ConsoleNavigateTarget = currentData.certificate
        ? {
          name: 'certificate_create',
          query: { id: String(currentData.certificate.id) },
        }
        : { name: 'certificate_create' };
      return {
        result: formWithMessage(actionId, composite, currentData, 'Opening certificate create…'),
        navigateTo,
      };
    }

    return {
      result: formWithError(actionId, composite, currentData, `Unknown SSL operation: ${operation}`),
    };
  } catch (error) {
    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        apiErrorMessage(error, 'SSL operation failed.'),
      ),
    };
  }
}

async function mutateDbManage(
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const actionId: ConsoleActionId = 'db.manage';
  const operation = payload && payload.operation ? String(payload.operation) : '';
  let currentData = await loadDbManageData(deps, composite);

  try {
    if (operation === 'create') {
      await deps.databaseRepository.createDatabase({
        name: String(payload.name || ''),
        password: String(payload.password || ''),
        passwordConf: String(payload.passwordConf || payload.password || ''),
        server: composite.website.originServer,
        websiteId: composite.website.id,
      });
      currentData = await loadDbManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Database creation started.'),
      };
    }

    if (operation === 'delete') {
      await deps.databaseRepository.deleteDatabase(Number(payload.databaseId));
      currentData = await loadDbManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Database deletion started.'),
      };
    }

    if (operation === 'sync') {
      await deps.databaseRepository.syncDatabaseSize(Number(payload.databaseId));
      currentData = await loadDbManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Database size sync started.'),
      };
    }

    if (operation === 'edit-users-navigate') {
      const databaseId = Number(payload.databaseId);
      const databases = await deps.databaseRepository.getByWebsiteId(composite.website.id);
      const database = (databases || []).find((item) => item.id === databaseId);
      const firstUser = database && database.users && database.users[0];
      if (!firstUser) {
        return {
          result: formWithError(
            actionId,
            composite,
            currentData,
            'No database users found to edit.',
          ),
        };
      }
      return {
        result: formWithMessage(actionId, composite, currentData, 'Opening database user edit…'),
        navigateTo: {
          name: 'database_user_edit',
          params: { userId: String(firstUser.id) },
        },
      };
    }

    return {
      result: formWithError(actionId, composite, currentData, `Unknown DB operation: ${operation}`),
    };
  } catch (error) {
    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        apiErrorMessage(error, 'Database operation failed.'),
      ),
    };
  }
}

async function mutateMailManage(
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const actionId: ConsoleActionId = 'mail.manage';
  const operation = payload && payload.operation ? String(payload.operation) : '';
  let currentData = await loadMailManageData(deps, composite);
  const unixUserId = composite.details && composite.details.unixUserId;

  try {
    if (operation === 'activate') {
      if (!unixUserId) {
        return {
          result: formWithError(actionId, composite, currentData, 'Website has no unix user.'),
        };
      }
      await deps.mailboxRepository.createMailbox({
        systemUserId: unixUserId,
        websiteId: composite.website.id,
        server: composite.website.originServer,
      });
      currentData = await loadMailManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Mailbox activation started.'),
      };
    }

    if (operation === 'create-user') {
      const mailboxId = currentData.mailbox && currentData.mailbox.id;
      if (!mailboxId || !unixUserId) {
        return {
          result: formWithError(
            actionId,
            composite,
            currentData,
            'Mailbox is not activated or unix user is missing.',
          ),
        };
      }
      const emailRaw = String(payload.email || '').trim();
      const address = emailRaw.indexOf('@') !== -1
        ? emailRaw
        : `${emailRaw}@${composite.website.domain}`;
      await deps.mailboxRepository.createMailboxUser({
        mailboxId,
        systemUserId: unixUserId,
        address,
        password: String(payload.password || ''),
        aliases: [],
        forwards: [],
      });
      currentData = await loadMailManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Mailbox user creation started.'),
      };
    }

    if (operation === 'toggle-user') {
      const mailboxId = currentData.mailbox && currentData.mailbox.id;
      if (!mailboxId) {
        return {
          result: formWithError(actionId, composite, currentData, 'Mailbox is not activated.'),
        };
      }
      const statusRaw = String(payload.status || '').toLowerCase();
      const status = statusRaw === 'disabled' || statusRaw === 'disable'
        ? MailboxStatus.DISABLED
        : MailboxStatus.ACTIVE;
      await deps.mailboxRepository.toggleMailbox(mailboxId, status);
      currentData = await loadMailManageData(deps, composite);
      return {
        result: formWithMessage(
          actionId,
          composite,
          currentData,
          status === MailboxStatus.DISABLED ? 'Mailbox disable started.' : 'Mailbox enable started.',
        ),
      };
    }

    if (operation === 'delete-user') {
      await deps.mailboxRepository.deleteMailboxUser(Number(payload.userId));
      currentData = await loadMailManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Mailbox user deletion started.'),
      };
    }

    if (operation === 'sync-quotas') {
      await deps.mailboxRepository.syncQuotas(composite.website.originServer);
      currentData = await loadMailManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Mailbox quota sync started.'),
      };
    }

    if (operation === 'edit-user-navigate') {
      return {
        result: formWithMessage(actionId, composite, currentData, 'Opening mailbox list…'),
        navigateTo: { name: 'mailbox_list' },
      };
    }

    return {
      result: formWithError(actionId, composite, currentData, `Unknown mail operation: ${operation}`),
    };
  } catch (error) {
    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        apiErrorMessage(error, 'Mail operation failed.'),
      ),
    };
  }
}

async function mutateFtpManage(
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const actionId: ConsoleActionId = 'ftp.manage';
  const operation = payload && payload.operation ? String(payload.operation) : '';
  let currentData = await loadFtpManageData(deps, composite);
  const unixUserId = (currentData && currentData.systemUserId)
    || (composite.details && composite.details.unixUserId);

  try {
    if (operation === 'create') {
      if (!unixUserId) {
        return {
          result: formWithError(actionId, composite, currentData, 'Website has no unix user.'),
        };
      }
      await deps.ftpUserRepository.createFtpUser({
        username: String(payload.username || ''),
        password: String(payload.password || ''),
        systemUserId: Number(unixUserId),
        server: composite.website.originServer,
        websiteId: composite.website.id,
        homeDir: String(payload.homeDir || ''),
      });
      currentData = await loadFtpManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'FTP user creation started.'),
      };
    }

    if (operation === 'update') {
      await deps.ftpUserRepository.updateFtpUser(Number(payload.userId), {
        password: String(payload.password || ''),
      });
      currentData = await loadFtpManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'FTP user update started.'),
      };
    }

    if (operation === 'delete') {
      await deps.ftpUserRepository.deleteFtpUser(Number(payload.userId));
      currentData = await loadFtpManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'FTP user deletion started.'),
      };
    }

    if (operation === 'connect') {
      const ftpUser = await deps.ftpUserRepository.findOneById(Number(payload.userId));
      if (!ftpUser) {
        return {
          result: formWithError(actionId, composite, currentData, 'FTP user not found.'),
        };
      }
      const jwtToken = deps.getJwt
        ? deps.getJwt().replace(/^Bearer\s+/i, '')
        : '';
      if (jwtToken) {
        document.cookie = `jwt=${jwtToken}; path=/; SameSite=Lax;`;
      }
      document.cookie = `ftpUserId=${ftpUser.id}; path=/; SameSite=Lax;`;
      document.cookie = `websiteId=${ftpUser.websiteId}; path=/; SameSite=Lax;`;
      document.cookie = `username=${ftpUser.username}; path=/; SameSite=Lax;`;
      const openUrl = '/file-manager/';
      return {
        result: formWithMessage(
          actionId,
          composite,
          currentData,
          `Opening file manager for ${ftpUser.username}…`,
        ),
        openUrl,
      };
    }

    return {
      result: formWithError(actionId, composite, currentData, `Unknown FTP operation: ${operation}`),
    };
  } catch (error) {
    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        apiErrorMessage(error, 'FTP operation failed.'),
      ),
    };
  }
}

async function mutateVhostManage(
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const actionId: ConsoleActionId = 'vhost.manage';
  const operation = payload && payload.operation ? String(payload.operation) : '';
  let currentData = await loadVhostManageData(deps, composite);

  try {
    const vhostId = currentData.vhost && currentData.vhost.id;
    if (!vhostId && operation !== '') {
      return {
        result: formWithError(actionId, composite, currentData, 'No vhost loaded.'),
      };
    }

    if (operation === 'update') {
      const aliases = Array.isArray(payload.aliases) ? payload.aliases : [];
      const phpVersion = parsePhpVersionNumber(String(payload.phpVersion || ''));
      await deps.originVhostRepository.updateVhost(Number(vhostId), {
        aliases,
        php: { version: phpVersion },
      });
      currentData = await loadVhostManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Vhost update started.'),
      };
    }

    if (operation === 'wildcard') {
      if (payload.remove) {
        await deps.originVhostRepository.removeWildCard(Number(vhostId));
        currentData = await loadVhostManageData(deps, composite);
        return {
          result: formWithMessage(actionId, composite, currentData, 'Wildcard remove started.'),
        };
      }
      await deps.originVhostRepository.createWildCard(Number(vhostId));
      currentData = await loadVhostManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Wildcard creation started.'),
      };
    }

    if (operation === 'delete-log') {
      const logs = (currentData.vhost && currentData.vhost.logs) || [];
      if (!logs.length) {
        return {
          result: formWithError(actionId, composite, currentData, 'No vhost logs to delete.'),
        };
      }
      const lastLog = logs[logs.length - 1];
      await deps.originVhostRepository.deleteLog(Number(lastLog.id));
      currentData = await loadVhostManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, `Deleted log #${lastLog.id}.`),
      };
    }

    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        `Unknown vhost operation: ${operation}`,
      ),
    };
  } catch (error) {
    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        apiErrorMessage(error, 'Vhost operation failed.'),
      ),
    };
  }
}

async function mutateRedisManage(
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  const actionId: ConsoleActionId = 'redis.manage';
  const operation = payload && payload.operation ? String(payload.operation) : '';
  let currentData = await loadRedisManageData(deps, composite);

  try {
    if (operation === 'enable') {
      await deps.redisCacheRepository.enableRedisCacheByWebsiteId(composite.website.id);
      currentData = await loadRedisManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Redis enable started.'),
      };
    }

    if (operation === 'disable') {
      await deps.redisCacheRepository.disableRedisCacheByWebsiteId(composite.website.id);
      currentData = await loadRedisManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Redis disable started.'),
      };
    }

    if (operation === 'sync') {
      await deps.redisCacheRepository.getRedisCacheSyncedStatusByWebsiteId(composite.website.id);
      currentData = await loadRedisManageData(deps, composite);
      return {
        result: formWithMessage(actionId, composite, currentData, 'Redis status sync requested.'),
      };
    }

    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        `Unknown Redis operation: ${operation}`,
      ),
    };
  } catch (error) {
    return {
      result: formWithError(
        actionId,
        composite,
        currentData,
        apiErrorMessage(error, 'Redis operation failed.'),
      ),
    };
  }
}

export async function executeConsoleActionMutation(
  actionId: ConsoleActionId,
  composite: WebsiteComposite,
  payload: any,
  deps: ConsoleActionExecutorDeps,
): Promise<ConsoleActionMutationOutcome> {
  try {
    switch (actionId) {
      case 'dns.manage':
        return mutateDnsManage(composite, payload, deps);
      case 'ssl.manage':
        return mutateSslManage(composite, payload, deps);
      case 'db.manage':
        return mutateDbManage(composite, payload, deps);
      case 'mail.manage':
        return mutateMailManage(composite, payload, deps);
      case 'ftp.manage':
        return mutateFtpManage(composite, payload, deps);
      case 'vhost.manage':
        return mutateVhostManage(composite, payload, deps);
      case 'redis.manage':
        return mutateRedisManage(composite, payload, deps);
      case 'staging.create':
      case 'staging.merge':
      case 'alias.create':
      case 'subdomain.create':
      case 'wp.install':
      case 'wp.uninstall':
      case 'website.enable':
      case 'website.disable':
      case 'website.rename':
      case 'website.delete':
      case 'website.recreate':
      case 'website.retry':
        return mutateWebsiteAction(actionId, composite, payload, deps);
      default:
        return {
          result: actionError(actionId, composite, `Unhandled action mutation: ${actionId}`),
        };
    }
  } catch (error) {
    // Permission and API failures stay inline — never throw redirects.
    return {
      result: actionError(
        actionId,
        composite,
        apiErrorMessage(error, 'Action failed.'),
      ),
    };
  }
}
